import { useMemo, useRef } from 'react';
import { Loader2, Wand2 } from 'lucide-react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from './ui/modal.js';
import type { QualityGateIssue } from '../lib/api/types.js';
import { diffWords, hasChanges, type DiffOp } from '../lib/diff.js';

/**
 * Review surface for a previewed selective QG fix batch. The edit pass has
 * already run (nothing saved); the operator reads the result and either
 * COMMITS the batch (persists + records history + stamps the issues) or
 * DISCARDS it (client-side only — the preview costs nothing to drop).
 *
 * 2026-08-31 (operator direction, LAC incident review): the two panes
 * highlight the diff on BOTH sides (removals struck on the left, additions
 * marked on the right) and scroll in sync, so the operator sees what changed
 * rather than hunting for it. When the batch carries FAQ fixes (the
 * structured FAQ pass), the changed answers render as their own diffed
 * section beneath the body.
 */

export interface FaqPair {
  question: string;
  answer: string;
}

interface QgFixBatchModalProps {
  issues: Array<QualityGateIssue & { index: number }>;
  before: string;
  after: string;
  faqBefore?: FaqPair[] | null;
  faqAfter?: FaqPair[] | null;
  committing: boolean;
  onCommit: () => void;
  onDiscard: () => void;
}

function DiffText({ ops, side }: { ops: DiffOp[]; side: 'before' | 'after' }) {
  return (
    <>
      {ops.map((op, i) => {
        if (op.type === 'eq') return <span key={i}>{op.text}</span>;
        if (op.type === 'del') {
          if (side === 'after') return null;
          return (
            <mark key={i} className="bg-red-100 text-red-900 line-through decoration-red-400 rounded-sm">
              {op.text}
            </mark>
          );
        }
        if (side === 'before') return null;
        return (
          <mark key={i} className="bg-emerald-100 text-emerald-900 rounded-sm">
            {op.text}
          </mark>
        );
      })}
    </>
  );
}

export function QgFixBatchModal({
  issues,
  before,
  after,
  faqBefore,
  faqAfter,
  committing,
  onCommit,
  onDiscard,
}: QgFixBatchModalProps) {
  const bodyOps = useMemo(() => diffWords(before, after), [before, after]);
  const bodyChanged = useMemo(() => hasChanges(bodyOps), [bodyOps]);

  // Per-pair FAQ diffs — only pairs whose answer actually changed render.
  const faqDiffs = useMemo(() => {
    if (!faqAfter) return [];
    const beforePairs = faqBefore ?? [];
    return faqAfter
      .map((pair, i) => ({
        question: pair.question,
        ops: diffWords(beforePairs[i]?.answer ?? '', pair.answer),
      }))
      .filter((d) => hasChanges(d.ops));
  }, [faqBefore, faqAfter]);

  // Synced scrolling: whichever pane the operator scrolls drives the other
  // proportionally. The `syncing` flag stops the mirrored scroll event from
  // bouncing back and fighting the pointer.
  const beforeRef = useRef<HTMLPreElement>(null);
  const afterRef = useRef<HTMLPreElement>(null);
  const syncing = useRef(false);
  const syncScroll = (from: HTMLPreElement | null, to: HTMLPreElement | null) => {
    if (!from || !to || syncing.current) return;
    syncing.current = true;
    const range = from.scrollHeight - from.clientHeight;
    const targetRange = to.scrollHeight - to.clientHeight;
    to.scrollTop = range > 0 ? (from.scrollTop / range) * targetRange : 0;
    requestAnimationFrame(() => {
      syncing.current = false;
    });
  };

  const paneBase =
    'text-xs whitespace-pre-wrap border rounded-lg p-3 max-h-80 overflow-y-auto';

  return (
    <Modal onClose={onDiscard} canClose={!committing} size="3xl">
      <ModalHeader>
        <div className="flex items-center gap-2">
          <Wand2 className="h-5 w-5 text-primary-600" />
          <h3 className="text-lg font-bold text-grey-900">
            Review fix batch ({issues.length} recommendation{issues.length === 1 ? '' : 's'})
          </h3>
        </div>
      </ModalHeader>
      <ModalBody>
        <div className="space-y-4">
          <ul className="space-y-1">
            {issues.map((iss) => (
              <li key={iss.index} className="text-xs text-grey-600">
                <span className="font-semibold uppercase">{iss.severity}</span>
                {' · '}{iss.source_skill} — {iss.fix}
              </li>
            ))}
          </ul>

          {bodyChanged ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wide text-grey-500 mb-1">Before</p>
                <pre
                  ref={beforeRef}
                  onScroll={() => syncScroll(beforeRef.current, afterRef.current)}
                  className={`${paneBase} bg-grey-50 border-grey-200`}
                >
                  <DiffText ops={bodyOps} side="before" />
                </pre>
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wide text-grey-500 mb-1">After</p>
                <pre
                  ref={afterRef}
                  onScroll={() => syncScroll(afterRef.current, beforeRef.current)}
                  className={`${paneBase} bg-primary-50/40 border-primary-200`}
                >
                  <DiffText ops={bodyOps} side="after" />
                </pre>
              </div>
            </div>
          ) : (
            <p className="text-xs text-grey-500 italic">No body changes in this batch.</p>
          )}

          {faqDiffs.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-grey-500">
                FAQ changes (structured FAQ pass)
              </p>
              {faqDiffs.map((d, i) => (
                <div key={i} className="border border-grey-200 rounded-lg p-3 space-y-2">
                  <p className="text-xs font-semibold text-grey-800">{d.question}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="text-xs whitespace-pre-wrap bg-grey-50 border border-grey-200 rounded p-2">
                      <DiffText ops={d.ops} side="before" />
                    </div>
                    <div className="text-xs whitespace-pre-wrap bg-primary-50/40 border border-primary-200 rounded p-2">
                      <DiffText ops={d.ops} side="after" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <p className="text-xs text-grey-500">
            Committing saves this content{faqDiffs.length > 0 ? ' and the corrected FAQ answers' : ''},
            records a history entry (rollbackable), and marks the selected recommendations as
            applied. The Quality Gate verdict will be flagged stale for the untouched items.
            Discarding saves nothing.
          </p>
        </div>
      </ModalBody>
      <ModalFooter>
        <button
          type="button"
          onClick={onDiscard}
          disabled={committing}
          className="px-4 py-2 text-sm font-medium text-grey-700 bg-white border border-grey-300 rounded-lg hover:bg-grey-50 disabled:opacity-50"
        >
          Discard batch
        </button>
        <button
          type="button"
          onClick={onCommit}
          disabled={committing}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          {committing ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Committing…
            </>
          ) : (
            <>Commit batch</>
          )}
        </button>
      </ModalFooter>
    </Modal>
  );
}
